import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  FaMicrophone,
  FaCalendarAlt,
  FaClock,
  FaMapMarkerAlt,
  FaCheckCircle,
  FaTimes,
  FaEye,
  FaYoutube,
  FaExternalLinkAlt,
  FaTrophy,
  FaStar,
  FaHistory,
  FaRedo,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import DescriptionViewer from "../../../Common/DescriptionViewer";
import ConferenceSubNav from "../../../Common/ConferenceSubNav";

const CompletedFreeConference = () => {
  const navigate = useNavigate();
  const [completedConferences, setCompletedConferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stats, setStats] = useState({
    totalCompleted: 0,
    thisMonth: 0,
  });
  const [selectedConference, setSelectedConference] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const fetchCompletedConferences = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/reporter/get/completed/conferences`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const conferences = response.data?.data || [];
      setCompletedConferences(conferences);

      const totalCompleted = conferences.length;
      const thisMonth = conferences.filter(conf => {
        const completedDate = new Date(conf.completedAt || conf.acceptedAt);
        const now = new Date();
        return completedDate.getMonth() === now.getMonth() && completedDate.getFullYear() === now.getFullYear();
      }).length;

      setStats({ totalCompleted, thisMonth });
    } catch (err) {
      setError("Failed to fetch completed conferences");
      toast.error("Failed to load records");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompletedConferences();
  }, []);

  const handleViewDetails = (conference) => {
    setSelectedConference(conference);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setSelectedConference(null);
    setShowModal(false);
  };

  const sortedConferences = [...completedConferences].sort((a, b) => {
    const dateA = new Date(a.completedAt || a.acceptedAt).getTime();
    const dateB = new Date(b.completedAt || b.acceptedAt).getTime();
    return dateB - dateA;
  });

  return (
    <div className="min-h-screen bg-[#f8fafc] pb-24 text-slate-900">
      {/* Premium Compact Header */}
      <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-slate-200/50 px-4 py-3">
        <div className="max-w-md mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="px-3 py-1 rounded-full bg-slate-50 border border-slate-200 text-slate-600 text-[10px] font-black uppercase tracking-tight hover:bg-slate-100 transition-all whitespace-nowrap shadow-sm"
            >
              ← Go Back
            </button>
            <div className="w-1.5 h-8 bg-gradient-to-b from-green-500 to-green-600 rounded-full" />
            <div>
              <h1 className="text-lg font-black text-slate-900 leading-tight">Reports Hub</h1>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider leading-none">Free Coverage</span>
                <div className="w-1 h-1 rounded-full bg-slate-300" />
                <span className="text-[9px] font-black text-green-600 italic leading-none">{stats.totalCompleted} Completed</span>
              </div>
            </div>
          </div>
          <button
            onClick={fetchCompletedConferences}
            disabled={loading}
            className="w-9 h-9 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-400 active:rotate-180 transition-all duration-500"
          >
            <FaRedo className={`text-[10px] ${loading ? "animate-spin text-green-500" : ""}`} />
          </button>
        </div>
      </div>

      <ConferenceSubNav type="free" />

      <div className="max-w-5xl mx-auto p-3 space-y-3">
        {/* Statistics Grid - Ultra Compact */}
        {!loading && !error && (
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-slate-900 rounded-2xl p-4 text-white relative overflow-hidden group">
              <div className="absolute -right-4 -bottom-4 w-12 h-12 bg-white/5 rounded-full blur-xl group-hover:bg-white/10 transition-all" />
              <p className="text-[8px] font-black text-slate-400 uppercase mb-1">Missions Done</p>
              <p className="text-sm font-black tracking-tighter tabular-nums text-green-400">{stats.totalCompleted}</p>
            </div>
            <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm">
              <p className="text-[8px] font-black text-slate-400 uppercase mb-1">This Month</p>
              <p className="text-sm font-black text-slate-900 leading-none">{stats.thisMonth}</p>
            </div>
          </div>
        )}

        {loading && (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <div className="relative">
              <div className="w-10 h-10 border-4 border-green-100 border-t-green-500 rounded-full animate-spin" />
              <FaHistory className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-green-500 text-[8px]" />
            </div>
            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Scanning Files...</p>
          </div>
        )}

        {/* Desktop View: Table (Ultra-Compact) */}
        {!loading && !error && (
          <div className="hidden lg:block bg-white rounded-[1.5rem] shadow-sm border border-slate-100 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50/50 text-slate-400 border-b border-slate-50">
                <tr>
                  <th className="px-5 py-4 text-left text-[9px] font-black uppercase tracking-widest">Mission</th>
                  <th className="px-5 py-4 text-left text-[9px] font-black uppercase tracking-widest">Deployment Date</th>
                  <th className="px-5 py-4 text-left text-[9px] font-black uppercase tracking-widest">Platform</th>
                  <th className="px-5 py-4 text-left text-[9px] font-black uppercase tracking-widest">Verified At</th>
                  <th className="px-5 py-4 text-center text-[9px] font-black uppercase tracking-widest">Intel</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {sortedConferences.map((conference) => (
                  <tr key={conference._id} className="hover:bg-slate-50/30 transition-colors">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center border border-green-100/50">
                          <FaCheckCircle className="text-green-600 text-[10px]" />
                        </div>
                        <span className="font-black text-slate-400 text-[10px] tabular-nums tracking-tighter italic truncate max-w-[150px]">{conference.conferenceDetails?.topic || "N/A"}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <p className="text-[10px] font-black text-slate-700 italic">{conference.conferenceDetails?.conferenceDate || "N/A"}</p>
                    </td>
                    <td className="px-5 py-3">
                      <p className="text-[10px] font-black text-blue-600 uppercase tracking-wider">{conference.proofDetails?.platform || "Official"}</p>
                    </td>
                    <td className="px-5 py-3 text-[10px] text-slate-400 font-bold italic tabular-nums">
                      {conference.completedAt ? new Date(conference.completedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : "N/A"}
                    </td>
                    <td className="px-5 py-3 text-center">
                      <button onClick={() => handleViewDetails(conference)} className="h-8 px-4 bg-slate-900 text-white rounded-xl text-[9px] font-black uppercase tracking-widest active:scale-95 transition-all flex items-center gap-1.5 mx-auto"><FaEye /> View</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Mobile View: High-Density Widgets */}
        {!loading && !error && (
          <div className="lg:hidden max-w-md mx-auto space-y-3">
            {completedConferences.length > 0 ? (
              sortedConferences.map((conference) => (
                <div key={conference._id} className="relative bg-white rounded-[1.75rem] border border-slate-100 p-1 shadow-sm active:scale-[0.99] transition-all overflow-hidden border-b-2 border-b-green-400/20">
                  <div className="bg-slate-50/40 rounded-[1.6rem] p-3.5 flex flex-col gap-3">
                    {/* Header */}
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-green-500 to-green-600 p-0.5 shadow-md shadow-green-100/50 flex-shrink-0">
                        <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                          <FaTrophy className="text-green-500 text-sm" />
                        </div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-[12px] font-black text-slate-900 leading-tight truncate pr-2 uppercase italic">{conference.conferenceDetails?.topic || "N/A"}</h3>
                        <div className="flex items-center justify-between mt-0.5">
                          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest tabular-nums italic">Code #{conference.conferenceId}</p>
                          <span className="px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest bg-green-100 text-green-800">Verified</span>
                        </div>
                      </div>
                    </div>

                    {/* Quick Data Grid */}
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-white rounded-xl p-2.5 border border-slate-100/50 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600 text-[10px]"><FaCalendarAlt /></div>
                        <div>
                          <p className="text-[7px] font-black text-slate-300 uppercase leading-none mb-1">Archived</p>
                          <p className="text-[10px] font-black text-slate-800 leading-none tabular-nums italic">{new Date(conference.completedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</p>
                        </div>
                      </div>
                      <div className="bg-white rounded-xl p-2.5 border border-slate-100/50 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center text-orange-600 text-[10px]"><FaClock /></div>
                        <div>
                          <p className="text-[7px] font-black text-slate-300 uppercase leading-none mb-1">Duration</p>
                          <p className="text-[10px] font-black text-slate-800 leading-none italic">{conference.proofDetails?.duration || "N/A"}</p>
                        </div>
                      </div>
                    </div>

                    {/* Single Action */}
                    <button onClick={() => handleViewDetails(conference)} className="w-full h-11 rounded-xl bg-slate-900 text-white shadow-lg shadow-slate-200/50 flex items-center justify-center gap-2 active:scale-95 transition-all text-[10px] font-black uppercase tracking-widest"><FaEye /> Inspect Official Case</button>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-24 px-10 text-center">
                <div className="w-16 h-16 rounded-[2rem] bg-white shadow-lg flex items-center justify-center mb-6">
                  <FaHistory className="text-slate-100 text-3xl" />
                </div>
                <h2 className="text-base font-black text-slate-900 tracking-tight">Archives Unavailable</h2>
                <p className="text-[9px] font-bold text-slate-400 mt-2 px-6 uppercase tracking-wider italic leading-relaxed">No completed coverage records found in our databases.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Official Record Modal */}
      {showModal && selectedConference && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" onClick={handleCloseModal} />
          <div className="relative w-full max-w-lg bg-white rounded-[2.5rem] overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300">
            <div className="bg-gradient-to-r from-green-600 to-emerald-700 p-6 text-white relative overflow-hidden">
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl" />
              <div className="relative z-10 flex justify-between items-start">
                <div>
                  <h3 className="text-xl font-black tracking-tighter leading-tight mb-1">Archived Mission Data</h3>
                  <p className="text-[9px] font-black uppercase tracking-[0.2em] text-green-100 italic">Coverage report & verified proof</p>
                </div>
                <button onClick={handleCloseModal} className="w-8 h-8 rounded-full bg-black/10 flex items-center justify-center hover:bg-black/20 transition-all"><FaTimes className="text-xs" /></button>
              </div>
            </div>

            <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
              {/* Proof Header */}
              <div className="bg-green-50/50 rounded-2xl p-4 border border-green-100 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FaCheckCircle className="text-green-600" />
                  <span className="text-[10px] font-black text-green-800 uppercase tracking-widest">Case Verified Successfully</span>
                </div>
              </div>

              {/* Basic Intel */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                  <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-2">Subject</p>
                  <p className="text-[11px] font-black text-slate-800">{selectedConference.conferenceDetails?.topic || "N/A"}</p>
                </div>
                <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                  <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-2">Deployment</p>
                  <p className="text-[11px] font-black text-slate-800 tabular-nums italic">{selectedConference.conferenceDetails?.conferenceDate}</p>
                </div>
              </div>

              {/* Coverage Footprint */}
              <div className="bg-blue-50/30 rounded-2xl p-4 border border-blue-100/50 space-y-3">
                <p className="text-[8px] font-black text-blue-400 uppercase tracking-widest">Digital Evidence</p>

                <div className="flex items-center justify-between bg-white p-3 rounded-xl border border-blue-50 shadow-sm">
                  <div className="flex items-center gap-3">
                    <FaYoutube className="text-rose-600" />
                    <div>
                      <p className="text-[10px] font-black text-slate-800 leading-none mb-1">{selectedConference.proofDetails?.channelName || "Official Link"}</p>
                      <p className="text-[9px] font-bold text-slate-400 leading-none uppercase">{selectedConference.proofDetails?.platform}</p>
                    </div>
                  </div>
                  {selectedConference.proofDetails?.videoLink && (
                    <a href={selectedConference.proofDetails.videoLink} target="_blank" rel="noopener noreferrer" className="h-8 px-4 bg-blue-600 text-white rounded-lg text-[9px] font-black uppercase flex items-center gap-1.5"><FaExternalLinkAlt className="text-[8px]" /> Watch</a>
                  )}
                </div>

                {selectedConference.proofDetails?.screenshot && (
                  <div className="relative group">
                    <img
                      src={selectedConference.proofDetails.screenshot.startsWith('http')
                        ? selectedConference.proofDetails.screenshot
                        : `${process.env.REACT_APP_API_BASE_URL}/uploads/conference-proofs/${selectedConference.proofDetails.screenshot.replace('\\', '/')}`}
                      alt="Record Visual"
                      className="w-full h-24 object-cover rounded-xl border border-white shadow-lg grayscale group-hover:grayscale-0 transition-all duration-500"
                    />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 bg-black/40 rounded-xl transition-all">
                      <a href={selectedConference.proofDetails.screenshot.startsWith('http')
                        ? selectedConference.proofDetails.screenshot
                        : `${process.env.REACT_APP_API_BASE_URL}/uploads/conference-proofs/${selectedConference.proofDetails.screenshot}`} target="_blank" rel="noreferrer" className="text-white text-xs font-black uppercase tracking-widest bg-green-600 px-4 py-2 rounded-lg">View Visual Intel</a>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="p-4 bg-slate-50 flex items-center justify-center px-6 border-t border-slate-100/50">
              <button onClick={handleCloseModal} className="h-10 w-full bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase tracking-[0.3em] shadow-lg shadow-slate-200">Close Archive</button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-center" theme="dark" hideProgressBar />
    </div>
  );
};

export default CompletedFreeConference;
